import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { PROGRAM_META } from "@/data/program";

export default function Hero() {
  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative pt-40 md:pt-48 pb-24 md:pb-32 overflow-hidden"
    >
      {/* Background image with heavy overlay */}
      <div className="absolute inset-0 -z-10">
        <img
          src="https://images.pexels.com/photos/3137073/pexels-photo-3137073.jpeg"
          alt=""
          className="w-full h-full object-cover opacity-25"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#FAFAF7]/95 via-[#FAFAF7]/85 to-[#FAFAF7]" />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12">
          <div className="md:col-span-8">
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="font-mono uppercase tracking-[0.24em] text-xs text-slate-500 mb-8"
              data-testid="hero-eyebrow"
            >
              Cohort · {PROGRAM_META.duration} · {PROGRAM_META.effort}
            </motion.p>

            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: "easeOut" }}
              className="font-serif text-5xl sm:text-6xl md:text-7xl lg:text-[104px] leading-[0.92] tracking-tighter text-slate-900"
              data-testid="hero-heading"
            >
              Forward Deployed<br />
              <span className="italic text-slate-700">Engineering</span>
              <span className="block text-slate-400 text-3xl md:text-4xl lg:text-5xl font-serif mt-6 tracking-tight">
                — an AI Industry Practitioner Program
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.25 }}
              className="mt-10 max-w-2xl text-lg md:text-xl text-slate-600 font-sans leading-relaxed"
            >
              A project-driven cohort for engineers, data scientists and
              practitioners who want to build, fine-tune, deploy and monitor AI
              systems in real industry environments — not toy notebooks.
            </motion.p>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="mt-12 flex flex-col sm:flex-row gap-4"
            >
              <Link
                to="/apply"
                data-testid="hero-apply-btn"
                className="inline-flex items-center justify-center bg-slate-900 text-white px-8 py-5 font-sans tracking-wide hover:bg-slate-800 transition-colors group"
              >
                Apply for the next cohort
                <span className="ml-3 group-hover:translate-x-1 transition-transform">→</span>
              </Link>
              <a
                href="#curriculum"
                data-testid="hero-curriculum-btn"
                className="inline-flex items-center justify-center border border-slate-300 hover:border-slate-900 text-slate-900 px-8 py-5 font-sans tracking-wide transition-colors"
              >
                View the curriculum
              </a>
            </motion.div>
          </div>

          <div className="md:col-span-4 flex md:justify-end">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.9, delay: 0.6 }}
              className="mt-6 md:mt-24 border-l border-slate-300 pl-8 md:max-w-[240px]"
              data-testid="hero-quote"
            >
              <p className="font-serif italic text-2xl md:text-3xl leading-snug text-slate-800">
                &ldquo;Deploy first.<br />
                Understand deeply.&rdquo;
              </p>
              <p className="mt-6 font-mono uppercase text-xs tracking-[0.24em] text-slate-500">
                Program philosophy
              </p>
            </motion.div>
          </div>
        </div>

        {/* Marquee-style meta rail */}
        <div className="mt-24 border-t border-slate-200 pt-8 grid grid-cols-2 md:grid-cols-4 gap-6 text-xs font-mono uppercase tracking-widest text-slate-500">
          <div>
            <span className="block text-slate-900 text-2xl font-serif normal-case tracking-tight">6</span>
            Phases · industry projects
          </div>
          <div>
            <span className="block text-slate-900 text-2xl font-serif normal-case tracking-tight">600+</span>
            Hands-on hours
          </div>
          <div>
            <span className="block text-slate-900 text-2xl font-serif normal-case tracking-tight">GCP · Azure</span>
            Production stacks
          </div>
          <div>
            <span className="block text-slate-900 text-2xl font-serif normal-case tracking-tight">Cohort</span>
            Instructor-led + labs
          </div>
        </div>
      </div>
    </section>
  );
}
